import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Box,
  Divider,
} from '@mui/material';
import { Search, FlightTakeoff } from '@mui/icons-material';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { HeaderV2, FooterV2 } from '../../components/layout';
import { API_BASE_URL as API_BASE } from '../../config/api';

const STATUS_COLOR = {
  PendingAgent: 'warning',
  Contacted: 'info',
  PriceConfirmed: 'success',
  Booked: 'success',
  Cancelled: 'error',
};

const FlightQuoteTrackPage = () => {
  const [searchParams] = useSearchParams();
  const [requestId, setRequestId] = useState(searchParams.get('requestId') || '');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState(null);

  const track = async (e) => {
    e?.preventDefault();
    setError('');
    setQuote(null);
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/flights/quote-requests/track`, {
        params: { requestId: requestId.trim(), phone: phone.trim() },
      });
      setQuote(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not find this request. Check ID and phone number.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <HeaderV2 />
      <Container maxWidth="sm" sx={{ py: { xs: 10, md: 12 } }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Track flight fare request
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Enter the request ID from your receipt and the phone number you used when submitting.
        </Typography>

        <Paper component="form" onSubmit={track} sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <TextField
              required
              label="Request ID"
              value={requestId}
              onChange={(e) => setRequestId(e.target.value)}
              placeholder="e.g. ac62e8f1-aba5-4bcf-a54a-d4dfe9720616"
            />
            <TextField
              required
              label="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="03040200070"
            />
            <Button type="submit" variant="contained" color="secondary" disabled={loading} startIcon={loading ? <CircularProgress size={18} /> : <Search />}>
              Track request
            </Button>
          </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {quote && (
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <FlightTakeoff color="secondary" />
              <Typography variant="h6" fontWeight={700}>
                {quote.origin} → {quote.destination}
              </Typography>
              <Chip label={quote.status} color={STATUS_COLOR[quote.status] || 'default'} size="small" />
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>{quote.statusLabel}</Alert>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Request ID: <strong>{quote.requestId}</strong> · PIN: <strong>{quote.trackingPin}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {quote.airlineName} {quote.flightNumber} · Depart {quote.departureDate}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2">
              Indicative price: <strong>{quote.shownCurrency} {Number(quote.shownAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
            </Typography>
            {quote.confirmedAmount && (
              <Typography variant="h6" color="secondary.main" sx={{ mt: 1 }}>
                Confirmed price: {quote.confirmedCurrency} {Number(quote.confirmedAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            )}
            {quote.agentMessage && (
              <Alert severity="success" sx={{ mt: 2 }}>{quote.agentMessage}</Alert>
            )}
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
              Submitted {new Date(quote.createdAt).toLocaleString()}
              {quote.updatedAt ? ` · Updated ${new Date(quote.updatedAt).toLocaleString()}` : ''}
            </Typography>
          </Paper>
        )}

        <Typography variant="body2" sx={{ mt: 3 }}>
          <RouterLink to="/v2/flights">← Back to flights</RouterLink>
        </Typography>
      </Container>
      <FooterV2 />
    </Box>
  );
};

export default FlightQuoteTrackPage;
